import { useEffect, useState } from 'react';

import { useGetApiV1CourseregistrationSharedClassStudentList } from '@/shared/api/generated';

import type { DropdownOption } from './useCompaniesDropdown';

/**
 * Busca server-side de turmas por nome, com debounce de 1 s e mínimo de 3 caracteres.
 *
 * Espelha {@link useCoursesDropdown}, porém usando o endpoint compartilhado de turmas
 * (`/courseregistration/shared/class-student-list`), que aceita o parâmetro `filter`.
 * Diferente de `useClassStudentsDropdown` (lista completa, sem busca), este hook é
 * voltado a um Autocomplete com pesquisa incremental.
 *
 * @param filter   - Texto digitado pelo usuário no campo de busca (default: `''`).
 * @param courseId - Filtra turmas do curso informado (opcional).
 * @returns Lista de turmas com estado de loading e sucesso da query.
 *
 * @example
 * ```tsx
 * const { classes, isQuerySuccess } = useClassStudentsSearchDropdown(classSearch);
 * ```
 */
export function useClassStudentsSearchDropdown(filter = '', courseId?: number) {
  const [debouncedFilter, setDebouncedFilter] = useState('');

  useEffect(() => {
    if (filter === '') {
      setDebouncedFilter('');
      return;
    }
    const timer = setTimeout(() => setDebouncedFilter(filter), 1000);
    return () => clearTimeout(timer);
  }, [filter]);

  const isReady = debouncedFilter.length >= 3;

  const { data, isLoading, isSuccess } = useGetApiV1CourseregistrationSharedClassStudentList(
    isReady
      ? { filter: debouncedFilter, ...(courseId !== undefined ? { courseId } : {}) }
      : undefined,
    { query: { enabled: isReady } },
  );

  const classes: readonly DropdownOption[] = (data ?? []).map(item => ({
    id: String(item.key ?? ''),
    name: item.value ?? '',
  }));

  return {
    classes,
    isLoading,
    isQuerySuccess: isSuccess,
  } as const;
}
